// DEV-ONLY preview harness for biome symbol-win animations. Mounted
// from App.tsx when the URL has `?dev=biomes`. Shows every `_win.webm`
// asset under apps/mobile/src/assets/reel/<biome>/, grouped by biome,
// plus any non-reel celebration webms (chest open, etc.) in an
// "Other" section. Art/design can scan the full set without spinning
// the slot.
//
// Auto-pauses videos that scroll out of view so dozens of simultaneous
// WebM streams don't grind low-end devices to a halt.

import { useEffect, useMemo, useRef, useState, type CSSProperties, type ReactNode } from 'react';
import { BIOMES } from '@goodtime/shared';

// Stage 3 install-excision (2026-06-06) — the eager globs that used
// to populate VIDEOS/PNGS/OTHER_VIDEOS/OTHER_PNGS dragged every reel
// + every other PNG/webm into the production bundle. This preview is
// a dev tool (mounted via ?dev=biomes), so we now resolve assets at
// RUNTIME via the AssetManifestClient — same path the player-facing
// code uses. The maps stay empty in production builds with no
// manifest loaded; designers wanting offline preview can set
// VITE_ASSET_CDN_BASE to a local Spring serve URL.
import { AssetManifestClient, initManifestClient } from '@goodtime/engine';

function buildAssetMaps(): {
  videos: Record<string, string>;
  pngs: Record<string, string>;
  otherVideos: Record<string, string>;
  otherPngs: Record<string, string>;
} {
  const manifest = AssetManifestClient.shared.getManifest();
  if (!manifest) {
    return { videos: {}, pngs: {}, otherVideos: {}, otherPngs: {} };
  }
  const videos: Record<string, string> = {};
  const pngs: Record<string, string> = {};
  const otherVideos: Record<string, string> = {};
  const otherPngs: Record<string, string> = {};
  for (const relPath of Object.keys(manifest.entries)) {
    const cdnUrl = AssetManifestClient.shared.resolveCdnUrl(relPath);
    if (!cdnUrl) continue;
    // Synthesise the legacy "../../assets/<path>" key the rest of
    // this file's regex matchers expect — minimises the diff. Classify
    // against `key` (not `relPath`): manifest keys are "reel/..." with
    // no leading slash, so the "/reel/" matchers only fire once the
    // "../../assets/" prefix is in place.
    const key = `../../assets/${relPath}`;
    if (/\/reel\/[^/]+\/[^/]+_win\.webm$/.test(key)) {
      videos[key] = cdnUrl;
    } else if (/\/reel\/[^/]+\/[^/]+\.png$/.test(key)) {
      pngs[key] = cdnUrl;
    } else if (relPath.endsWith('.webm')) {
      otherVideos[key] = cdnUrl;
    } else if (relPath.endsWith('.png')) {
      otherPngs[key] = cdnUrl;
    }
  }
  return { videos, pngs, otherVideos, otherPngs };
}

// Mutable so the component can rebuild them once the manifest finishes
// loading — in dev the manifest arrives asynchronously, so the initial
// module-load build below is empty until refreshAssetMaps() re-runs.
let VIDEOS: Record<string, string> = {};
let PNGS: Record<string, string> = {};
let OTHER_VIDEOS: Record<string, string> = {};
let OTHER_PNGS: Record<string, string> = {};
function refreshAssetMaps(): void {
  const m = buildAssetMaps();
  VIDEOS = m.videos;
  PNGS = m.pngs;
  OTHER_VIDEOS = m.otherVideos;
  OTHER_PNGS = m.otherPngs;
}
refreshAssetMaps();

/** Folder name under assets/reel/ → canonical biome id from @goodtime/shared.
 *  The reel folders use art-theme names; the BIOMES export uses world-map
 *  names. This is the only place that bridge lives. */
const FOLDER_TO_BIOME_ID: Record<string, string> = {
  forest: 'kingdom',
  underwater: 'cove',
  frost: 'frost',
  frontier: 'frontier',
  neon: 'neon',
  caravan: 'egypt', // Desert Caravan is Egypt's free hero slot
  // <scaffold:folder-biome> — scaffoldBiome appends a new biome's hero-folder→biome mapping above this line
  sherbet_square: 'candy',
};

interface SymbolEntry {
  /** Symbol name as it appears in the filename, sans the `_win` suffix. */
  symbol: string;
  /** Human-readable caption — underscores → spaces, title cased. */
  label: string;
  /** Resolved video URL, or null when no `_win.webm` exists yet
   *  (e.g. Neon symbols are PNG-only at the moment). */
  webmUrl: string | null;
  /** Resolved static PNG URL, or null when the paired PNG is missing. */
  pngUrl: string | null;
  /** Optional explicit filename overrides — for non-reel entries
   *  where the convention isn't `<symbol>.png` / `<symbol>_win.webm`
   *  (e.g. pickem chest uses chest_closed.png / chest_open.webm). */
  pngFilename?: string;
  webmFilename?: string;
}

interface BiomeGroup {
  folder: string;
  biomeId: string;
  /** Display name from BIOMES (e.g. "Greendale Kingdom"). */
  fullName: string;
  /** Short label — capitalised biome id (e.g. "Kingdom"). */
  shortName: string;
  symbols: SymbolEntry[];
  /** Count of symbols with an animation, used for section captions. */
  webmCount: number;
}

function humanise(symbol: string): string {
  return symbol
    .split('_')
    .map((w) => w.charAt(0).toUpperCase() + w.slice(1))
    .join(' ');
}

function buildGroups(): BiomeGroup[] {
  // folder/symbol → { webmUrl?, pngUrl? }. Merge across the two
  // globs so PNG-only symbols (Neon) and WEBM-only symbols both end
  // up as entries.
  const byKey = new Map<string, { webmUrl?: string; pngUrl?: string; folder: string; symbol: string }>();
  const get = (folder: string, symbol: string) => {
    const key = `${folder}/${symbol}`;
    let entry = byKey.get(key);
    if (!entry) {
      entry = { folder, symbol };
      byKey.set(key, entry);
    }
    return entry;
  };

  for (const [path, url] of Object.entries(VIDEOS)) {
    const match = path.match(/\/reel\/([^/]+)\/([^/]+)_win\.webm$/);
    if (!match) continue;
    get(match[1]!, match[2]!).webmUrl = url;
  }
  for (const [path, url] of Object.entries(PNGS)) {
    const match = path.match(/\/reel\/([^/]+)\/([^/]+)\.png$/);
    if (!match) continue;
    const name = match[2]!;
    // Skip the `_green` highlight variants — we want the canonical
    // base symbol (e.g. acorn.png), not the alternate.
    if (name.endsWith('_green')) continue;
    get(match[1]!, name).pngUrl = url;
  }

  const byFolder = new Map<string, SymbolEntry[]>();
  for (const entry of byKey.values()) {
    const list = byFolder.get(entry.folder) ?? [];
    list.push({
      symbol: entry.symbol,
      label: humanise(entry.symbol),
      webmUrl: entry.webmUrl ?? null,
      pngUrl: entry.pngUrl ?? null,
    });
    byFolder.set(entry.folder, list);
  }

  // Render in the canonical biome order (Kingdom → Cove → Frost →
  // Frontier → Neon), not whatever glob iteration order is.
  const canonical = BIOMES.map((biome) => {
    const folder = Object.keys(FOLDER_TO_BIOME_ID).find(
      (f) => FOLDER_TO_BIOME_ID[f] === biome.id,
    );
    const symbols: SymbolEntry[] = folder ? byFolder.get(folder) ?? [] : [];
    symbols.sort((a, b) => a.symbol.localeCompare(b.symbol));
    const webmCount = symbols.filter((s) => s.webmUrl !== null).length;
    return {
      folder: folder ?? biome.id,
      biomeId: biome.id,
      fullName: biome.name,
      shortName: biome.id.charAt(0).toUpperCase() + biome.id.slice(1),
      symbols,
      webmCount,
    };
  });

  // Any reel/ subfolder that isn't one of the five canonical biomes
  // is a themed-slot variant (royal, dragon, knights, wizard, …).
  // Surface each as its own group so the dev page reflects them
  // alongside the biomes. Pretty-names are looked up here so this
  // stays the only place the alt-theme nomenclature lives.
  const canonicalFolders = new Set(Object.keys(FOLDER_TO_BIOME_ID));
  const altPrettyNames: Record<string, string> = {
    royal: 'Royal Court',
    dragon: "Dragon's Hoard",
    knights: "Knights' Quest",
    wizard: "Wizard's Tower",
    gardens: 'Castle Gardens',
    dungeons: 'Castle Dungeons',
    // Cove themed slots
    kraken: "Kraken's Lair",
    map_fragment: 'Map Fragment',
    mermaid_cove: 'Mermaid Cove',
    stormy_seas: 'Stormy Seas',
    davy_jones: 'Davy Jones',
    buried_treasure: 'Buried Treasure',
    // Frostpeak themed slots
    avalanche: 'Avalanche',
    ice_cavern: 'Ice Cavern',
    aurora: 'Aurora',
    yeti: "Yeti's Den",
    hot_springs: 'Hot Springs',
    glacier_keep: 'Glacier Keep',
    // Dustwood themed slots
    gold_mine: 'Gold Mine',
    saloon: 'Saloon',
    bounty: 'Bounty',
    iron_horse: 'Iron Horse',
    stampede: 'Stampede',
    high_noon: 'High Noon',
    // Neon Heights themed slots
    arcade: 'Arcade',
    synthwave: 'Synthwave',
    cyber_heist: 'Cyber Heist',
    speedway: 'Speedway',
    skyline: 'Skyline',
    glitch: 'Glitch',
    // Ancient Egypt themed slots
    pharaoh_tomb: "Pharaoh's Tomb",
    pharaohs_riches: "Pharaoh's Riches",
    cleopatra: 'Cleopatra',
    book_of_dead: 'Book of the Dead',
    anubis: 'Anubis',
    eye_of_ra: 'Eye of Ra',
    // <scaffold:alt-pretty> — scaffoldBiome appends a new biome's non-hero slot pretty-names above this line
    lollipop_lane: 'Lollipop Lane',
    cocoa_river: 'Cocoa River',
    gummy_lab: 'Gummy Lab',
    fizz_factory: 'Fizz Factory',
    peppermint_pipeworks: 'Peppermint Pipeworks',
    sugarplum_vault: 'Sugarplum Vault',
  };
  const altGroups: BiomeGroup[] = [];
  for (const folder of byFolder.keys()) {
    if (canonicalFolders.has(folder)) continue;
    const symbols = byFolder.get(folder) ?? [];
    symbols.sort((a, b) => a.symbol.localeCompare(b.symbol));
    const webmCount = symbols.filter((s) => s.webmUrl !== null).length;
    const pretty = altPrettyNames[folder] ?? folder;
    altGroups.push({
      folder,
      biomeId: folder, // not a real biome id; used as the React key
      fullName: pretty,
      shortName: pretty,
      symbols,
      webmCount,
    });
  }
  altGroups.sort((a, b) => a.folder.localeCompare(b.folder));

  return [...canonical, ...altGroups];
}

/** Pair each non-reel webm with its most useful counterpart PNG.
 *  Heuristics, in priority order:
 *    1. Same folder, `<base>_closed.png` (e.g. chest_open.webm →
 *       chest_closed.png — the "before" state is the most informative
 *       comparison for a celebration/open animation).
 *    2. Same folder, same basename PNG (e.g. chest_open.webm →
 *       chest_open.png — the end state, which is the same as the
 *       animation's last frame).
 *    3. null if neither exists. */
function buildOtherGroup(): BiomeGroup | null {
  const entries: SymbolEntry[] = [];
  for (const [path, url] of Object.entries(OTHER_VIDEOS)) {
    if (path.includes('/reel/')) continue; // those go into BIOME groups above
    const match = path.match(/\/assets\/([^/]+(?:\/[^/]+)*)\/([^/]+)\.webm$/);
    if (!match) continue;
    const folder = match[1]!;
    const base = match[2]!;
    // Strip a trailing state suffix (e.g. _open, _opening, _win) so
    // the label reads as just the subject ("Chest" not "Chest Open").
    const symbol = base.replace(/_(open|opening|win|anim|celebrate)$/, '');

    const pairedClosed = findPng(folder, `${symbol}_closed`);
    const pairedSame = findPng(folder, base);
    const pngEntry = pairedClosed ?? pairedSame;
    entries.push({
      symbol,
      label: humanise(symbol),
      webmUrl: url,
      pngUrl: pngEntry?.url ?? null,
      pngFilename: pngEntry?.filename,
      webmFilename: `${base}.webm`,
    });
  }
  if (entries.length === 0) return null;
  entries.sort((a, b) => a.symbol.localeCompare(b.symbol));
  return {
    folder: 'other',
    biomeId: 'other',
    fullName: 'Pick-em & other celebrations',
    shortName: 'Other',
    symbols: entries,
    webmCount: entries.filter((s) => s.webmUrl !== null).length,
  };
}

function findPng(folder: string, basename: string): { url: string; filename: string } | null {
  const pathSuffix = `/assets/${folder}/${basename}.png`;
  for (const [path, url] of Object.entries(OTHER_PNGS)) {
    if (path.endsWith(pathSuffix)) {
      return { url, filename: `${basename}.png` };
    }
  }
  return null;
}

export function BiomeAnimationsPreview() {
  // The asset maps come from the manifest, which in dev loads
  // asynchronously. Force the Vite-served dev manifest (pass a null CDN
  // base) rather than VITE_ASSET_CDN_BASE: the prod CDN manifest has no
  // CORS header for the dev origin, so a CDN fetch just fails and leaves
  // the harness empty. The dev manifest is generated from src/assets and
  // is always reachable same-origin. Kick the load on mount, then
  // rebuild the maps + re-run the grouping memo once it lands.
  const [mapsVersion, setMapsVersion] = useState(0);
  useEffect(() => {
    let cancelled = false;
    void initManifestClient(null, {
      fallbackPrefix: import.meta.env.DEV ? '/src/assets' : undefined,
      devManifestUrl: import.meta.env.DEV
        ? '/__dev/asset-manifest.json'
        : undefined,
    }).then(() => {
      if (cancelled) return;
      refreshAssetMaps();
      setMapsVersion((v) => v + 1);
    });
    return () => {
      cancelled = true;
    };
  }, []);
  const groups = useMemo(() => {
    const biomeGroups = buildGroups();
    const other = buildOtherGroup();
    return other ? [...biomeGroups, other] : biomeGroups;
  }, [mapsVersion]);
  const totalVideos = groups.reduce((n, g) => n + g.webmCount, 0);

  // Pause off-screen videos so we're not decoding 37 streams at once.
  // A scroll listener on the fixed container is more robust than
  // IntersectionObserver — IO refused to fire inside the preview
  // iframe used for verification, and a scroll loop is trivially
  // cheap for a dev page that runs at low frequency.
  const scrollerRef = useRef<HTMLDivElement | null>(null);
  const videosRef = useRef<Set<HTMLVideoElement>>(new Set());
  const setVideoRef = (el: HTMLVideoElement | null) => {
    if (el) videosRef.current.add(el);
  };

  useEffect(() => {
    const scroller = scrollerRef.current;
    if (!scroller) return;
    const update = () => {
      // Compare each video against the scroller's own bounding box
      // rather than window.innerHeight — window.innerHeight can read
      // 0 in some iframe-hosted environments (e.g. the dev preview
      // tool), which would pause every video regardless of position.
      const sRect = scroller.getBoundingClientRect();
      for (const video of videosRef.current) {
        const r = video.getBoundingClientRect();
        const inView = r.bottom > sRect.top && r.top < sRect.bottom;
        if (inView && video.paused) {
          void video.play().catch(() => {
            // Autoplay can be blocked before a user gesture — fine
            // for a dev page; user can click to resume.
          });
        } else if (!inView && !video.paused) {
          video.pause();
        }
      }
    };
    scroller.addEventListener('scroll', update, { passive: true });
    window.addEventListener('resize', update);
    update();
    return () => {
      scroller.removeEventListener('scroll', update);
      window.removeEventListener('resize', update);
    };
  }, []);

  return (
    <div
      ref={scrollerRef}
      style={{
        position: 'fixed',
        inset: 0,
        background: '#060d22',
        color: '#e8edf7',
        overflowY: 'auto',
        fontFamily:
          '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif',
      }}
    >
      <header
        style={{
          padding: '24px 24px 12px',
          borderBottom: '1px solid rgba(255,255,255,0.08)',
        }}
      >
        <h1 style={{ margin: 0, fontSize: 22, fontWeight: 800, letterSpacing: 0.4 }}>
          Biome Animations
        </h1>
        <p style={{ margin: '4px 0 0', fontSize: 13, opacity: 0.6 }}>
          Every <code>_win.webm</code> asset, grouped by biome. Off-screen videos
          auto-pause.
        </p>
      </header>

      <div style={{ padding: '0 24px 80px' }}>
        {groups.map((group) => (
          <BiomeSection key={group.biomeId} group={group} setVideoRef={setVideoRef} />
        ))}
      </div>

      <DevModeBadge totalVideos={totalVideos} />
    </div>
  );
}

function BiomeSection({
  group,
  setVideoRef,
}: {
  group: BiomeGroup;
  setVideoRef: (el: HTMLVideoElement | null) => void;
}) {
  return (
    <section style={{ marginTop: 28 }}>
      <div
        style={{
          display: 'flex',
          alignItems: 'baseline',
          gap: 10,
          marginBottom: 12,
        }}
      >
        <h2 style={{ margin: 0, fontSize: 17, fontWeight: 700, letterSpacing: 0.3 }}>
          {group.shortName}
        </h2>
        <span style={{ fontSize: 13, opacity: 0.55 }}>{group.fullName}</span>
        <span style={{ fontSize: 12, opacity: 0.45, marginLeft: 'auto' }}>
          {sectionCaption(group)}
        </span>
      </div>

      {group.symbols.length === 0 ? (
        <div
          style={{
            padding: '24px 16px',
            background: 'rgba(255,255,255,0.03)',
            border: '1px dashed rgba(255,255,255,0.12)',
            borderRadius: 8,
            fontSize: 13,
            opacity: 0.6,
            textAlign: 'center',
          }}
        >
          No assets yet.
        </div>
      ) : (
        <div
          style={{
            display: 'grid',
            // Each card holds the static PNG + the animation side-by-
            // side, so cells need ~2x the width of a video-only card.
            gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
            gap: 12,
          }}
        >
          {group.symbols.map((entry) => (
            <SymbolCard key={entry.symbol} entry={entry} setVideoRef={setVideoRef} />
          ))}
        </div>
      )}
    </section>
  );
}

function sectionCaption(group: BiomeGroup): string {
  if (group.symbols.length === 0) return 'no assets yet';
  if (group.webmCount === 0) {
    return `${group.symbols.length} PNG-only — no animations yet`;
  }
  if (group.webmCount === group.symbols.length) {
    return `${group.webmCount} videos`;
  }
  return `${group.webmCount}/${group.symbols.length} animated`;
}

function SymbolCard({
  entry,
  setVideoRef,
}: {
  entry: SymbolEntry;
  setVideoRef: (el: HTMLVideoElement | null) => void;
}) {
  // Checkerboard so alpha-channel content is visible against either
  // light or dark pixels. Applied to both the PNG slot and the video
  // slot so they share a visual baseline.
  const checkerboard = {
    backgroundColor: '#1d2540',
    backgroundImage:
      'linear-gradient(45deg, #2a3354 25%, transparent 25%), ' +
      'linear-gradient(-45deg, #2a3354 25%, transparent 25%), ' +
      'linear-gradient(45deg, transparent 75%, #2a3354 75%), ' +
      'linear-gradient(-45deg, transparent 75%, #2a3354 75%)',
      backgroundSize: '16px 16px',
      backgroundPosition: '0 0, 0 8px, 8px -8px, -8px 0',
  } as const;

  return (
    <div
      style={{
        background: 'rgba(255,255,255,0.04)',
        border: '1px solid rgba(255,255,255,0.08)',
        borderRadius: 10,
        overflow: 'hidden',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <div style={{ display: 'flex', position: 'relative' }}>
        <AssetSlot label="PNG" style={checkerboard}>
          {entry.pngUrl ? (
            <img
              src={entry.pngUrl}
              alt={`${entry.symbol} symbol`}
              style={{ width: '100%', height: '100%', objectFit: 'contain', display: 'block' }}
            />
          ) : (
            <SlotPlaceholder text="no PNG" />
          )}
        </AssetSlot>
        <AssetSlot label="WEBM" style={checkerboard}>
          {entry.webmUrl ? (
            <video
              ref={setVideoRef}
              src={entry.webmUrl}
              loop
              muted
              playsInline
              preload="auto"
              controls
              style={{
                width: '100%',
                height: '100%',
                objectFit: 'contain',
                display: 'block',
              }}
            />
          ) : (
            <SlotPlaceholder text="no animation yet" />
          )}
        </AssetSlot>
      </div>
      <div
        style={{
          padding: '8px 10px',
          fontSize: 12,
          fontWeight: 600,
          letterSpacing: 0.2,
          borderTop: '1px solid rgba(255,255,255,0.06)',
        }}
      >
        {entry.label}
        <div style={{ fontSize: 10, opacity: 0.4, fontWeight: 400, marginTop: 2 }}>
          {entry.pngUrl ? (entry.pngFilename ?? `${entry.symbol}.png`) : '—'}
          {' · '}
          {entry.webmUrl ? (entry.webmFilename ?? `${entry.symbol}_win.webm`) : '—'}
        </div>
      </div>
    </div>
  );
}

function SlotPlaceholder({ text }: { text: string }) {
  return (
    <div
      style={{
        width: '100%',
        height: '100%',
        display: 'flex',
        alignItems: 'center',
        justifyContent: 'center',
        fontSize: 11,
        opacity: 0.5,
      }}
    >
      {text}
    </div>
  );
}

/** A square slot inside a card, with a small corner label that names
 *  which asset is mounted (PNG vs WEBM) — useful when the PNG and the
 *  animation diverge so the reader can tell them apart at a glance. */
function AssetSlot({
  label,
  style,
  children,
}: {
  label: string;
  style: CSSProperties;
  children: ReactNode;
}) {
  return (
    <div style={{ flex: 1, aspectRatio: '1 / 1', position: 'relative', ...style }}>
      {children}
      <span
        style={{
          position: 'absolute',
          top: 4,
          left: 4,
          padding: '1px 5px',
          fontSize: 9,
          fontWeight: 700,
          letterSpacing: 0.5,
          background: 'rgba(0,0,0,0.55)',
          color: 'rgba(255,255,255,0.75)',
          borderRadius: 3,
          pointerEvents: 'none',
        }}
      >
        {label}
      </span>
    </div>
  );
}

function DevModeBadge({ totalVideos }: { totalVideos: number }) {
  return (
    <div
      style={{
        position: 'fixed',
        bottom: 8,
        left: 8,
        zIndex: 200,
        padding: '4px 8px',
        background: 'rgba(0,0,0,0.7)',
        color: '#FFE49A',
        fontSize: 10,
        fontWeight: 700,
        letterSpacing: 1.2,
        textTransform: 'uppercase',
        border: '1px solid rgba(255,201,64,0.5)',
        borderRadius: 4,
        pointerEvents: 'none',
      }}
    >
      DEV · biome animations · {totalVideos} videos
    </div>
  );
}
